/**
 * Central site configuration — single source of truth for business, legal, and
 * contact data. Do NOT hardcode these values in components, SEO helpers, or
 * structured-data builders; import from here instead. (The static index.html is
 * the one place that can't import this; keep it in sync manually.)
 */
export const SITE_CONFIG = {
  name: 'Byteet',
  url: 'https://byteet.nl',
  defaultLocale: 'en',
  locales: ['en', 'nl'] as const,

  // Contact
  email: 'info@byteet.nl',
  phone: '+31627430754', // E.164, for tel: links and schema
  phoneDisplay: '+31 6 27430754',

  // Postal address
  address: {
    streetAddress: 'Grasweg 168',
    postalCode: '1031 HX',
    locality: 'Amsterdam',
    country: 'NL',
  },

  // Legal identifiers (NL)
  legal: {
    kvk: '98897497',
    btw: 'NL005360433B96',
  },

  // Social profiles
  social: {
    linkedin: 'https://www.linkedin.com/company/byteet',
    twitter: 'https://twitter.com/byteet',
  },
  twitterHandle: '@byteet',

  // Brand assets (site-relative)
  logo: '/logo-512.png',
  ogImage: '/og_image.jpg?v=2',

  availableLanguages: ['English', 'Dutch'],
  // Locale per site language, for dates and times (Intl).
  intlLocales: { en: 'en-GB', nl: 'nl-NL' },
  areaServed: 'Netherlands',

  // Base paths of the main pages, before localization. Shared components build
  // links as getLocalizedRoute(SITE_CONFIG.routes.x, language), so they stay
  // identical across byteet and byteet_md, whose slugs differ.
  routes: {
    services: '/services',
    portfolio: '/cases',
    articles: '/articles',
    about: '/about',
    contact: '/contact',
    privacy: '/privacy-policy',
    terms: '/terms-of-service',
    book: '/book',
  },

  // Online booking (/book). Read by api/calendar-availability.ts,
  // api/calendar-book.ts and src/pages/Book.tsx, so changing a rule here
  // changes it everywhere. Times are wall-clock times in `timezone`.
  booking: {
    timezone: 'Europe/Amsterdam',
    /** ISO weekdays: 1 = Monday … 7 = Sunday. */
    workingDays: [1, 2, 3, 4, 5],
    /** First slot starts here; the last slot ends by `dayEnd`. HH:MM, 24h. */
    dayStart: '09:00',
    dayEnd: '17:00',
    slotMinutes: 30,
    /** Kept free before and after every busy block in the calendar. */
    bufferMinutes: 15,
    /** Earliest bookable slot is this far from now. */
    minNoticeHours: 4,
    /** Days ahead (counted from today in `timezone`) that can be booked. */
    horizonDays: 21,
    /** Event summary: "<prefix>: <name> (<company>)". */
    titlePrefix: 'Byteet intro call',
  },
} as const;

export type SiteConfig = typeof SITE_CONFIG;

/**
 * Canonical service categories — the single source of truth shared by the
 * contact forms (service selection) and the portfolio category filter. These
 * ids are stable and language-independent: they are what gets submitted to the
 * backend and stored in Jira. Display labels come from the `serviceOptions.*`
 * translation keys. Keep these in sync with the nine service pages
 * (ServicePage.tsx) and the homepage summaries.
 */
export const SERVICE_OPTIONS = [
  { id: 'web-software-development', labelKey: 'serviceOptions.webSoftwareDevelopment' },
  { id: 'mobile-app-development', labelKey: 'serviceOptions.mobileAppDevelopment' },
  { id: 'crm-development', labelKey: 'serviceOptions.crmDevelopment' },
  { id: 'erp-systems', labelKey: 'serviceOptions.erpSystems' },
  { id: 'ai-automation', labelKey: 'serviceOptions.aiAutomation' },
  { id: 'data-databases', labelKey: 'serviceOptions.dataDatabases' },
  { id: 'cloud-infrastructure', labelKey: 'serviceOptions.cloudInfrastructure' },
  { id: 'cybersecurity-identity', labelKey: 'serviceOptions.cybersecurityIdentity' },
  { id: 'managed-it-support', labelKey: 'serviceOptions.managedItSupport' },
] as const;

/** The "Other" option is always rendered last and reveals a free-text field. */
export const OTHER_SERVICE_OPTION = { id: 'other', labelKey: 'serviceOptions.other' } as const;

export type ServiceOptionId =
  | (typeof SERVICE_OPTIONS)[number]['id']
  | typeof OTHER_SERVICE_OPTION.id;
